/**
 * The ⋯ menu on a table row.
 *
 * The panel renders in a portal on document.body and is positioned from the
 * trigger's rect: every table on this app sits inside a card or scroll pane
 * that clips (`overflow: hidden` / `auto`), and an absolutely positioned panel
 * gets sliced off there — worst on the last row, where Delete is what goes
 * missing. Fixed positioning also lets it flip above the trigger when the
 * viewport runs out below.
 *
 * Closes on outside click, on Escape, and on choosing something — a menu you
 * have to dismiss twice is worse than no menu. Destructive items are marked and
 * sit at the bottom, behind a divider, so the mouse never passes over Delete on
 * its way somewhere else.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export interface MenuItem {
  label: string
  onSelect: () => void
  danger?: boolean
  disabled?: boolean
  hint?: string
}

/** Gap between the trigger and the panel, and the margin we keep off the edges. */
const GAP = 4
const EDGE = 8

export function RowMenu({ items, label = 'Actions' }: { items: MenuItem[]; label?: string }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  const place = useCallback(() => {
    const anchor = trigger.current?.getBoundingClientRect()
    if (!anchor) return
    const width = panel.current?.offsetWidth || 208
    const height = panel.current?.offsetHeight || 0
    const below = window.innerHeight - anchor.bottom - GAP - EDGE
    const above = anchor.top - GAP - EDGE
    // Flip up only when there is genuinely more room there.
    const up = height > below && above > below
    setPos({
      top: up ? Math.max(EDGE, anchor.top - GAP - height) : anchor.bottom + GAP,
      left: Math.min(
        Math.max(EDGE, anchor.right - width),
        Math.max(EDGE, window.innerWidth - width - EDGE),
      ),
      maxHeight: Math.max(120, up ? above : below),
    })
  }, [])

  useLayoutEffect(() => {
    if (!open) return setPos(null)
    place()
  }, [open, place])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (trigger.current?.contains(target) || panel.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        trigger.current?.focus()
      }
    }
    // Any ancestor scrolling moves the trigger, so follow it (capture catches
    // scroll on the table pane, which does not bubble).
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  const safe = items.filter((i) => !i.danger)
  const destructive = items.filter((i) => i.danger)

  return (
    <div className="rowmenu" onClick={(e) => e.stopPropagation()}>
      <button
        ref={trigger}
        className="rowmenu-trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
      >
        ⋯
      </button>

      {open &&
        createPortal(
          <div
            ref={panel}
            className="rowmenu-panel"
            role="menu"
            aria-label={label}
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              maxHeight: pos?.maxHeight,
              visibility: pos ? 'visible' : 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {safe.map((item) => (
              <MenuButton key={item.label} item={item} onDone={() => setOpen(false)} />
            ))}
            {safe.length > 0 && destructive.length > 0 && <div className="rowmenu-divider" />}
            {destructive.map((item) => (
              <MenuButton key={item.label} item={item} onDone={() => setOpen(false)} />
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}

function MenuButton({ item, onDone }: { item: MenuItem; onDone: () => void }) {
  return (
    <button
      className="rowmenu-item"
      role="menuitem"
      data-danger={item.danger || undefined}
      disabled={item.disabled}
      onClick={() => {
        onDone()
        item.onSelect()
      }}
    >
      <span>{item.label}</span>
      {item.hint && <span className="rowmenu-hint">{item.hint}</span>}
    </button>
  )
}

/** The search box every table page shares. */
export function TableSearch({
  value,
  onChange,
  placeholder,
  count,
  children,
}: {
  value: string
  onChange: (next: string) => void
  placeholder: string
  count?: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="row" style={{ marginBottom: 12 }}>
      <input
        className="input"
        style={{ maxWidth: 320 }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={placeholder}
      />
      {count !== undefined && <span className="small muted">{count}</span>}
      <span className="spacer" />
      {children}
    </div>
  )
}
